import {
  GiftCouponForRedemption,
  GiftCouponForRedemptionDashboard,
} from '@shared/api/giftCoupon/giftCoupon.interface';
import { fileBucketUrl } from '@shared/utils/fileUtils';

/**
 * 초과발송(위험) 여부 판단
 * - redeemSequence(N번째 요청)가 purchasedCount(구매 수량)보다 큰 경우
 *   구매한 갯수보다 많이 신청·발급된 위험 건으로 간주합니다.
 */
export const isOverIssued = (coupon: GiftCouponForRedemption): boolean => {
  const dc = coupon as GiftCouponForRedemptionDashboard;
  const seq = dc.redeemSequence;
  const summary = dc.orderCouponSummary;
  if (seq == null || summary == null) return false;
  return seq > summary.purchasedCount;
};

export const formatDateTime = (
  date: Date | string | undefined,
  includeSeconds = false,
) => {
  if (!date) return '-';
  const d = new Date(date);
  const y = String(d.getFullYear()).slice(2);
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  const h = String(d.getHours()).padStart(2, '0');
  const min = String(d.getMinutes()).padStart(2, '0');
  if (includeSeconds) {
    const sec = String(d.getSeconds()).padStart(2, '0');
    return `${y}.${m}.${day} ${h}:${min}:${sec}`;
  }
  return `${y}.${m}.${day} ${h}:${min}`;
};

export const formatDate = (date: Date | string | undefined) => {
  if (!date) return '-';
  const d = new Date(date);
  const y = String(d.getFullYear()).slice(2);
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}.${m}.${day}`;
};

export const formatPhone = (phone: string | undefined) => {
  if (!phone) return '-';
  const numbers = phone.replace(/[^0-9]/g, '');
  if (numbers.length === 11) {
    return `${numbers.slice(0, 3)}-${numbers.slice(3, 7)}-${numbers.slice(7)}`;
  }
  return phone;
};

export const getItemName = (coupon: GiftCouponForRedemption) => {
  if (typeof coupon.giftItem === 'object') {
    return coupon.giftItem?.name || coupon.giftItem?.giftItemInfo?.name || '-';
  }
  return '-';
};

export const getChoiceUnitName = (coupon: GiftCouponForRedemption) => {
  if (coupon.selectedChoiceUnit && typeof coupon.selectedChoiceUnit === 'object') {
    return coupon.selectedChoiceUnit.name;
  }
  return null;
};

export const getPriceInfo = (coupon: GiftCouponForRedemption) => {
  if (typeof coupon.giftItem === 'object') {
    return {
      priceRegular: coupon.giftItem?.priceRegular || 0,
      priceRetail: coupon.giftItem?.priceRetail || 0,
      priceWhole: coupon.giftItem?.priceWhole || 0,
      priceRebate: coupon.giftItem?.priceRebate || 0,
    };
  }
  return { priceRegular: 0, priceRetail: 0, priceWhole: 0, priceRebate: 0 };
};

export const getOrdererInfo = (coupon: GiftCouponForRedemption) => {
  const order = typeof coupon.giftOrder === 'object' ? coupon.giftOrder : null;
  if (!order) return { name: '-', phone: '-', purchasedAt: undefined };
  return {
    name: order.bugo?.member?.fullName || '-',
    phone: order.bugo?.member?.phoneNumber || '-',
    purchasedAt: order.paymentDetail?.purchasedAt,
  };
};

export const getImageUrl = (coupon: GiftCouponForRedemption) => {
  if (
    coupon.selectedChoiceUnit &&
    typeof coupon.selectedChoiceUnit === 'object' &&
    coupon.selectedChoiceUnit.image
  ) {
    const img = coupon.selectedChoiceUnit.image;
    const imgUrl = typeof img === 'object' && 'url' in img ? img.url : img;
    if (imgUrl) return fileBucketUrl(imgUrl, 'w200');
  }
  if (typeof coupon.giftItem === 'object') {
    const giftItemImg = coupon.giftItem?.image;
    if (giftItemImg) {
      const imgUrl =
        typeof giftItemImg === 'object' && 'url' in giftItemImg
          ? giftItemImg.url
          : giftItemImg;
      if (imgUrl) return fileBucketUrl(imgUrl, 'w200');
    }
    const giftItemInfoImg = coupon.giftItem?.giftItemInfo?.image;
    if (giftItemInfoImg) {
      const imgUrl =
        typeof giftItemInfoImg === 'object' && 'url' in giftItemInfoImg
          ? giftItemInfoImg.url
          : giftItemInfoImg;
      if (imgUrl) return fileBucketUrl(imgUrl, 'w200');
    }
  }
  return '';
};

/**
 * 쿠폰의 "우리 판매가"(소비자가) — 상조 정산 수수료·환불액의 기준.
 * 1순위: 구매 당시 동결 salePriceRetail (백엔드 denormalize).
 * 폴백(백필 전·구서버): giftItem.priceRetail(상품 판매가) → 선택정가·정가(최후).
 * ※ 선택유닛 priceRegular(선택정가)은 판매가가 아니므로 최후 폴백일 뿐 정상 경로가 아니다.
 */
export const getCouponSaleRetail = (
  couponId: string,
  coupons: GiftCouponForRedemption[],
): number => {
  const coupon = coupons.find((c) => c._id === couponId);
  if (!coupon) return 0;
  if (coupon.salePriceRetail != null) return coupon.salePriceRetail;
  if (typeof coupon.giftItem === 'object' && coupon.giftItem?.priceRetail) {
    return coupon.giftItem.priceRetail;
  }
  if (
    coupon.selectedChoiceUnit &&
    typeof coupon.selectedChoiceUnit === 'object' &&
    coupon.selectedChoiceUnit.priceRegular
  ) {
    return coupon.selectedChoiceUnit.priceRegular;
  }
  if (typeof coupon.giftItem === 'object') {
    return coupon.giftItem?.priceRegular || 0;
  }
  return 0;
};
